"use client";

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import type { ZodSchema } from "zod";

const LOCAL_STORAGE_CHANGE_EVENT = "sfc:local-storage-change";
const getServerSnapshot = () => null;

export type LocalStorageValidator<T> = (value: unknown) => value is T;

export type UseLocalStorageOptions<T> = {
  /** Reject a valid JSON value whose runtime shape is not safe for this caller. */
  validate?: LocalStorageValidator<T>;
};

export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

export const isBooleanRecord = (
  value: unknown,
): value is Record<string, boolean> =>
  value != null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.values(value).every((item) => typeof item === "boolean");

function localStorageOrNull(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readRaw(key: string): string | null {
  const storage = localStorageOrNull();

  if (storage == null) {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function isZodSchema(value: unknown): value is ZodSchema<unknown> {
  return (
    value != null &&
    typeof value === "object" &&
    "parse" in value &&
    typeof value.parse === "function" &&
    "safeParse" in value &&
    typeof value.safeParse === "function"
  );
}

/**
 * With no caller-supplied validator, preserve the broad runtime shape implied
 * by the default. This catches the dangerous cases (an object where a list was
 * expected, a string where a boolean was expected) without pretending generic
 * TypeScript information exists at runtime.
 */
function matchesDefaultShape(value: unknown, fallback: unknown): boolean {
  if (fallback == null) {
    return true;
  }

  if (Array.isArray(fallback)) {
    return Array.isArray(value);
  }

  if (typeof fallback === "object") {
    return value != null && typeof value === "object" && !Array.isArray(value);
  }

  return typeof value === typeof fallback;
}

function parseStoredValue<T>(
  raw: string | null,
  fallback: T,
  validate?: LocalStorageValidator<T>,
  schema?: ZodSchema<T>,
): T {
  if (raw == null) {
    return fallback;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (schema != null) {
      return schema.parse(parsed);
    }

    const valid = validate?.(parsed) ?? matchesDefaultShape(parsed, fallback);
    return valid ? (parsed as T) : fallback;
  } catch {
    return fallback;
  }
}

function jsonIdentity(value: unknown): unknown {
  try {
    return JSON.stringify(value) ?? value;
  } catch {
    return value;
  }
}

export function parseLocalStorageValue<T>(
  raw: string | null,
  fallback: T,
  validate?: LocalStorageValidator<T>,
): T {
  return parseStoredValue(raw, fallback, validate);
}

function subscribeToKey(key: string, onStoreChange: () => void): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const onStorage = (event: StorageEvent) => {
    const localStorage = localStorageOrNull();

    if (event.storageArea != null && event.storageArea !== localStorage) {
      return;
    }

    // A null key means another tab called localStorage.clear().
    if (event.key == null || event.key === key) {
      onStoreChange();
    }
  };

  const onLocalStorageChange = (event: Event) => {
    if ((event as CustomEvent<string>).detail === key) {
      onStoreChange();
    }
  };

  window.addEventListener("storage", onStorage);
  window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, onLocalStorageChange);

  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(
      LOCAL_STORAGE_CHANGE_EVENT,
      onLocalStorageChange,
    );
  };
}

function emitLocalStorageChange(key: string): void {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<string>(LOCAL_STORAGE_CHANGE_EVENT, { detail: key }),
  );
}

function removeStoredValue(storage: Storage, key: string): boolean {
  try {
    storage.removeItem(key);
    return true;
  } catch (error) {
    console.warn("[storage] remove failed", { key, error: String(error) });
    return false;
  }
}

function writeStoredValue(
  storage: Storage,
  key: string,
  value: unknown,
): boolean {
  try {
    const serialized = JSON.stringify(value);

    if (serialized == null) {
      return false;
    }

    storage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.warn("[storage] write failed", { key, error: String(error) });
    return false;
  }
}

/**
 * Read and write validated localStorage state without letting storage failures
 * escape into user interactions. Storage is the source of truth, so mounted
 * consumers and browser tabs stay synchronized. Functional setters re-read at
 * call time to avoid overwriting a newer value from another consumer.
 *
 * The schema overload preserves the original shared-hook API. The options
 * overload is useful when callers already have a lightweight type guard.
 */
export function useLocalStorage<T>(
  key: string,
  schema: ZodSchema<T>,
  defaultValue: T,
): readonly [T, Dispatch<SetStateAction<T>>];
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  options?: UseLocalStorageOptions<T>,
): readonly [T, Dispatch<SetStateAction<T>>];

export function useLocalStorage<T>(
  key: string,
  schemaOrDefault: ZodSchema<T> | T,
  defaultOrOptions?: T | UseLocalStorageOptions<T>,
): readonly [T, Dispatch<SetStateAction<T>>] {
  const schema = isZodSchema(schemaOrDefault)
    ? (schemaOrDefault as ZodSchema<T>)
    : undefined;
  const defaultValue = (
    schema == null ? schemaOrDefault : defaultOrOptions
  ) as T;
  const validate =
    schema == null
      ? (defaultOrOptions as UseLocalStorageOptions<T> | undefined)?.validate
      : undefined;

  // Defaults are commonly array/object literals or derived collections. Give
  // equivalent JSON values a stable reference so parsing stored JSON does not
  // manufacture a new result, and retrigger consumers' effects, on every
  // unrelated render. A genuinely changed default still takes effect.
  const defaultValueIdentity = jsonIdentity(defaultValue);
  const stableDefaultValueRef = useRef({
    identity: defaultValueIdentity,
    value: defaultValue,
  });

  if (
    !Object.is(stableDefaultValueRef.current.identity, defaultValueIdentity)
  ) {
    stableDefaultValueRef.current = {
      identity: defaultValueIdentity,
      value: defaultValue,
    };
  }

  const stableDefaultValue = stableDefaultValueRef.current.value;

  const getSnapshot = useCallback(() => readRaw(key), [key]);
  const subscribe = useCallback(
    (onStoreChange: () => void) => subscribeToKey(key, onStoreChange),
    [key],
  );
  const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const value = useMemo(
    () => parseStoredValue(raw, stableDefaultValue, validate, schema),
    [raw, stableDefaultValue, validate, schema],
  );

  const defaultValueRef = useRef(defaultValue);
  defaultValueRef.current = defaultValue;
  const validatorRef = useRef(validate);
  validatorRef.current = validate;
  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (action) => {
      const storage = localStorageOrNull();

      if (storage == null) {
        return;
      }

      const current = parseStoredValue(
        readRaw(key),
        defaultValueRef.current,
        validatorRef.current,
        schemaRef.current,
      );
      const next =
        typeof action === "function"
          ? (action as (previous: T) => T)(current)
          : action;

      const persisted =
        next == null
          ? removeStoredValue(storage, key)
          : writeStoredValue(storage, key, next);

      if (persisted) {
        emitLocalStorageChange(key);
      }
    },
    [key],
  );

  return [value, setValue] as const;
}
